// src/components/ecommerce/CategoryManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Grid,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Package,
  Tag,
  Image as ImageIcon,
  Upload,
  Save,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Folder,
  FolderOpen,
  Move,
  Copy,
  Star,
  TrendingUp,
  BarChart3,
  Users,
  DollarSign,
  Clock,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { firestoreService } from '../../services/firestoreService';
import { FileUpload } from '../common/FileUpload';

export const CategoryManager = ({ storeId, storeData, onUpdate }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [categoryStats, setCategoryStats] = useState({});

  const [uploadStates, setUploadStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadCategories();
    loadCategoryStats();
  }, [storeId]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await ecommerceService.getCategories(storeId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryStats = async () => {
    try {
      const stats = await ecommerceService.getCategoryStats(storeId);
      setCategoryStats(stats);
    } catch (error) {
      console.error('Error loading category stats:', error);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    const productCount = categoryStats[categoryId]?.productCount || 0;
    
    if (productCount > 0) {
      const confirmed = window.confirm(
        `This category has ${productCount} products. Deleting it will remove the category from all products. Are you sure?`
      );
      if (!confirmed) return;
    } else {
      const confirmed = window.confirm('Are you sure you want to delete this category?');
      if (!confirmed) return;
    }

    try {
      await ecommerceService.deleteCategory(categoryId, storeId);
      await loadCategories();
      await loadCategoryStats();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleCategorySave = async () => {
    await loadCategories();
    await loadCategoryStats();
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const handleReorderCategories = async (fromIndex, toIndex) => {
    try {
      const reorderedCategories = [...categories];
      const [movedCategory] = reorderedCategories.splice(fromIndex, 1);
      reorderedCategories.splice(toIndex, 0, movedCategory);

      // Update order values
      const updates = reorderedCategories.map((category, index) => ({
        id: category.id,
        order: index
      }));

      await ecommerceService.reorderCategories(storeId, updates);
      await loadCategories();
    } catch (error) {
      console.error('Error reordering categories:', error);
    }
  };

  const toggleCategoryExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categories</h2>
          <p className="text-gray-600">
            Organize your products into categories for better navigation
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => ecommerceService.exportCategories(storeId)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Upload className="h-4 w-4" />
            Export
          </button>

          <button
            onClick={handleAddCategory}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Category
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Categories</p>
              <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Grid className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Most Popular</p>
              <p className="text-lg font-bold text-gray-900">
                {Object.keys(categoryStats).length > 0 
                  ? Object.entries(categoryStats)
                      .sort((a, b) => (b[1].productCount || 0) - (a[1].productCount || 0))[0]?.[1]?.name || 'N/A'
                  : 'N/A'
                }
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(categoryStats).reduce((sum, cat) => sum + (cat.productCount || 0), 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Empty Categories</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(categoryStats).filter(cat => (cat.productCount || 0) === 0).length}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : filteredCategories.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-5">Category</div>
              <div className="col-span-2">Products</div>
              <div className="col-span-2">Revenue</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Actions</div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredCategories.map((category, index) => {
              const stats = categoryStats[category.id] || {};
              const isExpanded = expandedCategories.has(category.id);
              
              return (
                <div
                  key={category.id}
                  className="hover:bg-gray-50 transition-colors"
                  draggable
                  onDragStart={() => setDraggedCategory(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggedCategory !== null && draggedCategory !== index) {
                      handleReorderCategories(draggedCategory, index);
                      setDraggedCategory(null);
                    }
                  }}
                >
                  <div className="p-4 grid grid-cols-12 gap-4 items-center">
                    {/* Category Info */}
                    <div className="col-span-5 flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {category.imageUrl ? (
                          <img
                            src={category.imageUrl}
                            alt={category.name}
                            className="h-10 w-10 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Folder className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 truncate">
                            {category.name}
                          </h3>
                          {category.featured && (
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          )}
                        </div>
                        
                        {category.description && (
                          <p className="text-sm text-gray-500 truncate">
                            {category.description}
                          </p>
                        )}
                        
                        {category.tags && category.tags.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {category.tags.slice(0, 3).map(tag => (
                              <span
                                key={tag}
                                className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                            {category.tags.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{category.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Product Count */}
                    <div className="col-span-2">
                      <div className="text-sm font-medium text-gray-900">
                        {stats.productCount || 0} products
                      </div>
                      <div className="text-xs text-gray-500">
                        {stats.activeProducts || 0} active
                      </div>
                    </div>

                    {/* Revenue */}
                    <div className="col-span-2">
                      <div className="text-sm font-medium text-gray-900">
                        R{(stats.totalRevenue || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Avg: R{(stats.averagePrice || 0).toFixed(2)}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        category.status === 'active' 
                          ? 'text-green-700 bg-green-100' 
                          : 'text-gray-700 bg-gray-100'
                      }`}>
                        {category.status || 'active'}
                      </span>
                      
                      {category.order !== undefined && (
                        <div className="text-xs text-gray-500 mt-1">
                          Order: {category.order}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="col-span-1">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => toggleCategoryExpanded(category.id)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Expand Details"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {isExpanded && category.subcategories && category.subcategories.length > 0 && (
                    <div className="bg-gray-50 border-t border-gray-200">
                      <div className="px-4 py-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Folder className="h-4 w-4" />
                          Subcategories ({category.subcategories.length})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {category.subcategories
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((subcategory) => (
                              <div
                                key={subcategory.id}
                                className="bg-white px-3 py-2 rounded border border-gray-200 text-sm"
                              >
                                <span className="font-medium text-gray-900">{subcategory.name}</span>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Performance</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Views:</span>
                              <span className="font-medium">{stats.views || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Clicks:</span>
                              <span className="font-medium">{stats.clicks || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Conversion:</span>
                              <span className="font-medium">
                                {stats.conversionRate ? `${stats.conversionRate}%` : '0%'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Top Products</h4>
                          <div className="space-y-2 text-sm">
                            {stats.topProducts?.slice(0, 3).map((product, i) => (
                              <div key={i} className="flex justify-between">
                                <span className="text-gray-600 truncate">{product.name}</span>
                                <span className="font-medium">{product.sales}</span>
                              </div>
                            )) || (
                              <div className="text-gray-500">No products yet</div>
                            )}
                          </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Quick Actions</h4>
                          <div className="space-y-2">
                            <button className="w-full text-left px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100">
                              View Products
                            </button>
                            <button className="w-full text-left px-3 py-2 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100">
                              Add Product
                            </button>
                            <button className="w-full text-left px-3 py-2 text-sm bg-purple-50 text-purple-700 rounded hover:bg-purple-100">
                              View Analytics
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Grid className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? 'No categories found' : 'No categories yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchQuery 
              ? 'Try adjusting your search terms'
              : 'Create categories to organize your products and make them easier to find'
            }
          </p>
          {!searchQuery && (
            <button
              onClick={handleAddCategory}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Create First Category
            </button>
          )}
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <CategoryForm
          category={editingCategory}
          storeId={storeId}
          isOpen={showCategoryForm}
          onClose={() => setShowCategoryForm(false)}
          onSave={handleCategorySave}
        />
      )}
    </div>
  );
};

// Category Form Component
const CategoryForm = ({ category, storeId, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    status: 'active',
    featured: false,
    tags: [],
    seoTitle: '',
    seoDescription: '',
    order: 0,
    subcategories: []
  });

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');

  const [uploadStates, setUploadStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [newSubcategory, setNewSubcategory] = useState('');

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        status: category.status || 'active',
        featured: category.featured || false,
        tags: category.tags || [],
        seoTitle: category.seoTitle || '',
        seoDescription: category.seoDescription || '',
        order: category.order || 0,
        subcategories: category.subcategories || []
      });
    }
  }, [category]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleImageUpload = async (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, image: error }));
      return;
    }

    if (!file) return;

    setUploadStates(prev => ({ ...prev, image: { uploading: true, error: null } }));

    try {
      const result = await firestoreService.uploadFile(
        file, 
        `pb-${storeId}/categories/images`
      );
      
      setFormData(prev => ({ ...prev, imageUrl: result.url }));
      setUploadStates(prev => ({ ...prev, image: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error('Error uploading category image:', error);
      setUploadStates(prev => ({ 
        ...prev, 
        image: { uploading: false, error: error.message } 
      }));
      setValidationErrors(prev => ({ ...prev, image: error.message }));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (category) {
        await ecommerceService.updateCategory(storeId, category.id, formData);
      } else {
        await ecommerceService.addCategory(storeId, formData);
      }

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setSaving(false);
    }
  };

  // Add subcategory management functions
  const addSubcategory = () => {
    if (newSubcategory.trim() && !formData.subcategories.some(sub => sub.name === newSubcategory.trim())) {
      setFormData(prev => ({
        ...prev,
        subcategories: [
          ...prev.subcategories,
          {
            id: `sub_${Date.now()}`, // Simple ID generation
            name: newSubcategory.trim(),
            order: prev.subcategories.length
          }
        ]
      }));
      setNewSubcategory('');
    }
  };

  const removeSubcategory = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, index) => index !== indexToRemove)
    }));
  };

  const updateSubcategory = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      subcategories: prev.subcategories.map((sub, i) => 
        i === index ? { ...sub, [field]: value } : sub
      )
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {category ? 'Edit Category' : 'Add New Category'}
            </h2>
            <p className="text-gray-600">
              {category ? 'Update category information' : 'Create a new category for your products'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Category Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter category name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Describe this category..."
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Image
            </label>
            
            <FileUpload
              onFileSelect={handleImageUpload}
              currentFile={formData.imageUrl}
              onRemove={() => handleInputChange('imageUrl', '')}
              accept="image/*"
              maxSize={5}
              label="Upload Category Image"
              uploading={uploadStates.image?.uploading || false}
              error={uploadStates.image?.error || validationErrors.image}
              showPreview={true}
            />
            
            <p className="text-xs text-gray-500 mt-1">
              Upload an image to represent this category
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Add a tag"
              />
              <button
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Subcategories Section */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Subcategories
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Add subcategories to help organize products within this category
            </p>

            {/* Existing Subcategories */}
            {formData.subcategories.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.subcategories.map((subcategory, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      value={subcategory.name}
                      onChange={(e) => updateSubcategory(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Subcategory name"
                    />
                    <input
                      type="number"
                      value={subcategory.order}
                      onChange={(e) => updateSubcategory(index, 'order', parseInt(e.target.value) || 0)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Order"
                      min="0"
                    />
                    <button
                      onClick={() => removeSubcategory(index)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                      title="Remove subcategory"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Subcategory */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newSubcategory}
                onChange={(e) => setNewSubcategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addSubcategory()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Add a subcategory (e.g., Laptops, Tablets)"
              />
              <button
                onClick={addSubcategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => handleInputChange('featured', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium">Featured Category</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {Object.keys(errors).length > 0 && (
              <span className="text-red-600">Please fix the errors above</span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || Object.keys(errors).length > 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Saving...' : (category ? 'Update Category' : 'Create Category')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};